import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Laptop, Monitor, Smartphone } from 'lucide-react';
import type { z } from 'zod';
import type { schemas } from '@shared/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, ConfirmDialog } from '@/components/ui/misc';
import { ErrorState, TableSkeleton } from '@/components/states';
import { PageHeader } from '@/components/layout/PageHeader';
import { callApi } from '@/lib/apiClient';
import { useAuthActions } from './useAuth';

type SessionRecord = z.infer<typeof schemas.identity.sessionRecord>;

/**
 * Active sessions and devices.
 *
 * The one place a user can see, and end, a session they do not recognise. The list comes
 * from the refresh-token store, so it reflects what can actually still be rotated into a
 * new access token — not a cosmetic "login history".
 */
export function SessionsPage() {
  const [sessions, setSessions] = useState<SessionRecord[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const { logout } = useAuthActions();

  // A counter rather than a refetch function: the fetch then lives entirely inside the
  // effect, where it can be cancelled on unmount, and nothing writes state synchronously
  // during a render pass.
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    void callApi('auth.sessions')
      .then((res) => {
        if (cancelled) return;
        setSessions(res.data);
        setLoadError(false);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadCount]);

  async function revoke(id: string) {
    setRevoking(id);
    try {
      await callApi('auth.revokeSession', { params: { id } });
      // Re-read rather than splicing locally: revoking one token can cascade (a whole
      // family), and the server is the only thing that knows what is left.
      reload();
    } finally {
      setRevoking(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Sessions and devices"
        description="Every device currently signed in to your account. End any session you do not recognise, then change your password."
        actions={
          sessions && sessions.length > 1 ? (
            <Button variant="secondary" onClick={() => setConfirmAll(true)}>
              Sign out everywhere
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Active sessions</CardTitle>
          <CardDescription>
            A session ends on its own after 7 days without use, or immediately when you
            revoke it here.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loadError ? (
            <ErrorState title="Could not load your sessions" onRetry={reload} />
          ) : !sessions ? (
            <div className="px-6 pb-6">
              <TableSkeleton rows={3} columns={3} />
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {sessions.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-6 py-4">
                  <DeviceIcon userAgent={s.userAgent} />

                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-body font-medium">
                      {describeDevice(s.userAgent)}
                      {s.current && <Badge variant="brand">This device</Badge>}
                    </p>
                    <p className="text-caption text-fg-muted">
                      {s.ipAddress ?? 'Unknown address'} · last used {relative(s.lastUsedAt)} ·
                      signed in {relative(s.createdAt)}
                    </p>
                  </div>

                  {s.current ? (
                    <Button variant="ghost" onClick={() => void logout()}>
                      Sign out
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      className="text-danger"
                      loading={revoking === s.id}
                      onClick={() => void revoke(s.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title="Sign out on every device?"
        description="Every session, including this one, will end. You will need to sign in again."
        confirmLabel="Sign out everywhere"
        destructive
        onConfirm={() => void logout(true)}
      />
    </>
  );
}

// ── Describing a device ─────────────────────────────────────────────────────────
//
// A user agent is a weak signal, so these say only what can be read confidently and
// never claim more. "Unknown device" is a better answer than a wrong one.

function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';

  const browser =
    /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : /Safari\//.test(userAgent) ? 'Safari'
    : null;

  const platform =
    /BM-Work-Desktop/.test(userAgent) ? 'BM-Work desktop app'
    : /Windows/.test(userAgent) ? 'Windows'
    : /Macintosh|Mac OS/.test(userAgent) ? 'macOS'
    : /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad/.test(userAgent) ? 'iOS'
    : /Linux/.test(userAgent) ? 'Linux'
    : null;

  if (browser && platform) return `${browser} on ${platform}`;
  return browser ?? platform ?? 'Unknown device';
}

function DeviceIcon({ userAgent }: { userAgent: string | null }) {
  const ua = userAgent ?? '';
  const Icon =
    /Android|iPhone/.test(ua) ? Smartphone
    : /BM-Work-Desktop/.test(ua) ? Monitor
    : Laptop;

  return (
    <span
      aria-hidden
      className="grid size-9 shrink-0 place-items-center rounded-md bg-subtle text-fg-muted"
    >
      <Icon className="size-4" />
    </span>
  );
}

function relative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return 'at an unknown time';
  }
}
